<div class="reset-page screen-bottom-halo">
  <div class="logo-container">
    <img class="logo-icon" src="assets/images/explain-logo-900.svg" alt="explain_logo" />
    <img class="logo-text" src="assets/images/explain-text-900.svg" alt="explain_text" />
  </div>

  <div class="reset-card">
    <!-- Loading state -->
    @if (loading) {
      <div class="loading-dots">
        <span class="loading-dot"></span>
        <span class="loading-dot"></span>
        <span class="loading-dot"></span>
      </div>
      <h2 class="card-title loading-title">{{ 'login.title' | translate }}</h2>
    }

    <!-- Login form state -->
    @if (!loading) {
      <div class="card-header">
        <h2 class="card-title">{{ 'login.title' | translate }}</h2>
        @if (!discoveryResult) {
          <p class="card-subtitle">{{ 'login.sub-title' | translate }}</p>
        }
      </div>
      @if (!discoveryResult) {
        <form [formGroup]="discoveryForm" (ngSubmit)="discover()" class="auth-form">
          <div class="form-field">
            <label for="email" class="field-label">{{ 'login.email' | translate | uppercase }}</label>
            <input
              type="email"
              formControlName="email"
              class="field-input"
              [ngClass]="{ 'field-input--error': discoveryForm.dirty && df['email'].errors }"
              autocomplete="email"
              data-cy="email-input"
              autofocus
            />
          </div>
          <div class="error-container">
            @if (isError) {
              <div class="error-banner">
                <i class="ph ph-warning-circle error-icon"></i>
                <span class="error-text">{{ errorMessage | translate }}</span>
              </div>
            }
          </div>
          <div class="form-actions">
            <button id="discovery-submit" [disabled]="discoveryForm.invalid" class="btn-submit btn-login-cta">
              {{ 'login.continue' | translate }}
            </button>
          </div>
        </form>
        <div class="or-separator">
          <div class="or-separator-line"></div>
          <div>{{ 'login.or' | translate }}</div>
          <div class="or-separator-line"></div>
        </div>
        <div class="social-buttons">
          <button type="button" class="btn-social" (click)="socialSignin('google')" data-cy="google-signin">
            <img src="assets/images/google-logo.svg" class="social-icon" alt="Google" />
            <span>{{ 'login.sign-in-with-google' | translate }}</span>
          </button>
          <button type="button" class="btn-social" (click)="socialSignin('microsoft')" data-cy="microsoft-signin">
            <img src="assets/images/microsoft-logo.svg" class="social-icon" alt="Microsoft" />
            <span>{{ 'login.sign-in-with-microsoft' | translate }}</span>
          </button>
        </div>
        <p class="cgu-notice">
          {{ 'register.cgu-notice' | translate }}
          <a class="cgu-notice-link" [href]="cguUrl" target="_blank" rel="noopener noreferrer">{{ 'register.cgu-link' | translate }}</a
          >.
        </p>
      } @else if (!discoveryResult.authorization_url) {
        <form [formGroup]="loginForm" (ngSubmit)="newLogin()" class="auth-form">
          <div class="form-field">
            <div class="d-flex gap-2 align-items-center justify-content-center">
              <div class="email-display">{{ df['email'].value }}</div>
              <div (click)="discoveryResult = undefined; isError = false" class="change-email">{{ 'login.change-email' | translate }}</div>
            </div>
          </div>
          @if (discoveryResult.has_password !== false) {
            <div class="form-field">
              <label for="password" class="field-label">{{ 'login.password' | translate | uppercase }}</label>
              <div class="password-input-wrapper">
                <input
                  [type]="showPassword ? 'text' : 'password'"
                  formControlName="password"
                  class="field-input"
                  id="password"
                  [ngClass]="{ 'field-input--error': isError }"
                  autocomplete="current-password"
                  data-cy="password-input"
                  autofocus
                />
                <button
                  type="button"
                  class="password-toggle"
                  (click)="toggleFieldTextType()"
                  [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'"
                >
                  @if (showPassword) {
                    <img class="toggle-icon" src="assets/images/hide-password-icon.svg" alt="icon-hide" />
                  }
                  @if (!showPassword) {
                    <img class="toggle-icon" src="assets/images/show-password-icon.svg" alt="icon-show" />
                  }
                </button>
              </div>
              @if (submitted && f['password'].errors) {
                <div class="invalid-feedback">
                  @if (f['password'].errors['required']) {
                    <div>Password is required</div>
                  }
                </div>
              }
            </div>
            <div>
              <ngx-turnstile
                [siteKey]="environment.captchaPublicKey"
                #turnstileComponent
                action="sign_in"
                theme="light"
                (resolved)="handleCaptcha($event)"
                (errored)="handleError()"
              />
              @if (isError) {
                <div class="error-banner">
                  <i class="ph ph-warning-circle error-icon"></i>
                  <span class="error-text">{{ errorMessage | translate }}</span>
                </div>
              }
            </div>
            <div class="form-actions">
              <button type="submit" [disabled]="loading || loginForm.invalid" class="btn-submit btn-login-cta" data-cy="login-submit">
                {{ 'login.login' | translate }}
              </button>
            </div>
          } @else {
            <div class="info-banner">
              <i class="ph ph-info info-icon"></i>
              @if (discoveryResult.sso && !discoveryResult.social_providers?.length) {
                <span class="info-text">{{ 'login.no-password-info-sso' | translate }}</span>
              } @else {
                <span class="info-text">{{
                  (discoveryResult.social_providers!.length === 1
                    ? 'login.no-password-info-social'
                    : 'login.no-password-info-social-multiple'
                  ) | translate: { provider: discoveryResult.social_providers![0] | titlecase }
                }}</span>
              }
            </div>
          }
          @if (discoveryResult.social_providers?.length) {
            @if (discoveryResult.has_password !== false) {
              <div class="or-separator">
                <div class="or-separator-line"></div>
                <div>{{ 'login.or' | translate }}</div>
                <div class="or-separator-line"></div>
              </div>
            }
            <div class="social-buttons">
              @for (provider of discoveryResult.social_providers; track provider) {
                <button type="button" class="btn-social" (click)="socialSignin(provider)">
                  <img [src]="'assets/images/' + provider + '-logo.svg'" class="social-icon" [alt]="provider" />
                  <span>{{ 'login.sign-in-with-' + provider | translate }}</span>
                </button>
              }
            </div>
          } @else if (discoveryResult.sso && discoveryResult.slug) {
            @if (discoveryResult.has_password !== false) {
              <div class="or-separator">
                <div class="or-separator-line"></div>
                <div>{{ 'login.or' | translate }}</div>
                <div class="or-separator-line"></div>
              </div>
            }
            <div>
              <button type="button" class="btn-submit btn-login-outline" (click)="loginWithSSO()">
                {{ 'login.sign-in-with-sso' | translate }}
              </button>
            </div>
          }
          <div class="d-flex justify-content-center pt-4">
            <a [routerLink]="['/login/forgot-password']" class="link-secondary" data-cy="forgot-password-link">
              {{ (discoveryResult.has_password === false ? 'login.set-password' : 'login.password-forgotten') | translate }}
            </a>
          </div>
        </form>
      } @else {
        <div class="auth-form d-flex flex-column align-items-center justify-content-center">
          <div class="loading-dots">
            <span class="loading-dot"></span>
            <span class="loading-dot"></span>
            <span class="loading-dot"></span>
          </div>
          <div>{{ 'login.redirecting' | translate }}</div>
        </div>
      }
    }
  </div>
</div>
